// "Who's on this phone?" Children tap themselves; grown-ups need their PIN (mandatory, WI-16).
// A phone joins a family once: by setting the family up on it (WI-21; "just me" for a solo
// traveller, WI-37), with an invite code from the parent, or with the family's recovery code.
// A phone belongs to someone (its resident, WI-17): a grown-up picked on a child's phone is in
// parent mode. The choice is remembered locally so the app opens straight in, offline or not.
// See docs/ORGANISER_PLAN.md §4.
import { create } from 'zustand'
import type { Person, SessionResponse } from '../../shared/events'
import type { AuthError, StartFamilyRequest } from '../../shared/family'
import { api, HttpError, Offline } from './api'
import { kvGet, kvSet } from './db'
import { deviceId, deviceLabel, newDeviceId } from './device'
import { stampUnownedOutbox, syncNow } from './sync'

/**
 * Whether this phone is in a family (WI-22): not known until the server has answered (or, offline,
 * until the cached family says it was). The gate shows nothing heavy (no 3D) while it's unknown.
 */
export type LinkState = 'unknown' | 'linked' | 'needs-link'

/** The person part of a session answer (what's cached as `person`). */
function personOf(r: Person | SessionResponse): Person {
  const person: Partial<SessionResponse> = { ...r }
  delete person.resident
  delete person.parentMode
  return person as Person
}

/**
 * A last-touch time kept outside React, so a reload resumes a countdown rather than restarting it.
 * localStorage can be missing or throw (private mode): then it lasts a page.
 */
function touchStore(key: string) {
  let fallback: number | null = null
  return {
    read(): number | null {
      try {
        const v = Number(localStorage.getItem(key))
        return v > 0 ? v : null
      } catch {
        return fallback
      }
    },
    write(at: number) {
      fallback = at
      try {
        localStorage.setItem(key, String(at))
      } catch {
        // the in-memory copy will do
      }
    },
    clear() {
      fallback = null
      try {
        localStorage.removeItem(key)
      } catch {
        // nothing stored
      }
    },
  }
}

/** Parent mode's last touch (WI-17). */
export const parentTouch = touchStore('tq.parentTouch')
/** The last time a grown-up used the grown-up screens, or typed their PIN, on their own phone (B3). */
export const organiserTouch = touchStore('tq.organiserTouch')

/** An invite opened as /join#CODE, captured before the router rewrites the URL. */
let joinCode: string | null =
  typeof location !== 'undefined' && location.pathname === '/join' && location.hash.length > 1 ? decodeURIComponent(location.hash.slice(1)) : null

/** The server's reason for refusing a sign-in or link, if that's what this error is. */
export function authError(e: unknown): AuthError | null {
  return e instanceof HttpError && e.body && typeof e.body === 'object' && 'error' in e.body ? (e.body as AuthError) : null
}

/** People cached by an older version have no `kind`; fill it in from the role. */
export function normalisePerson(p: Person): Person {
  return {
    ...p,
    kind: p.kind ?? (p.role === 'organiser' ? 'admin' : 'child'),
    ageBand: p.ageBand ?? null,
    figure: p.figure ?? null,
    pin: p.pin ?? false,
  }
}

/** "Set it up" without the phone's own fields (the store adds this phone's id and label). */
export type StartFamily = Omit<StartFamilyRequest, 'deviceId' | 'label'>

/** Posts to a link-like route, retrying under a new phone id if this one was unlinked before (409). */
async function asThisPhone<T>(path: string, fields: object): Promise<T> {
  const label = await deviceLabel()
  const body = (id: string) => JSON.stringify({ ...fields, deviceId: id, label })
  try {
    return await api<T>(path, { method: 'POST', body: body(await deviceId()) })
  } catch (e) {
    if (!(e instanceof HttpError && e.status === 409)) throw e
    return api<T>(path, { method: 'POST', body: body(await newDeviceId()) })
  }
}

interface SessionState {
  person: Person | null
  ready: boolean
  people: Person[] | null
  /** In a family, not yet (show the Welcome screen), or not known yet (WI-22). */
  linkState: LinkState
  /** Whose phone this is (WI-17), as the server last said. */
  resident: string | null
  /** A code from a /join# link, to prefill (and try) on the join screen. */
  pendingCode: string | null
  restore(): Promise<void>
  loadPeople(): Promise<void>
  /** Links this phone with an invite code (or none, on a local dev server). */
  link(code?: string): Promise<void>
  /**
   * Sets a family (or just me) up on this phone. The server signs the parent in, but the app
   * opens only after `enter`, once the recovery code has been seen.
   */
  startFamily(req: StartFamily): Promise<{ person: Person; recoveryCode: string }>
  /** Every phone lost: the recovery code links this phone and signs the parent in. */
  recover(code: string): Promise<void>
  /** Opens the app as someone the server has already signed in (after setting up). */
  enter(person: Person): Promise<void>
  choose(personId: string, pin?: string): Promise<void>
  /**
   * Settings › Switch (WI-17): someone picked on the Who screen. Re-picking yourself does nothing;
   * only a different person signs the current one out and the new one in.
   */
  switchTo(personId: string, pin?: string): Promise<void>
  /** "Make this <name>'s phone" (WI-17): the only way to hand a phone over. Needs a recent PIN. */
  makeResident(personId: string): Promise<void>
  forget(): Promise<void>
  /** Re-issues the server session for the remembered person (the cookie expired or was cleared). */
  renew(): Promise<boolean>
  /** Replaces the signed-in person's cached record (after a PIN or name change). */
  refreshMe(): Promise<void>
  /**
   * Parent mode (B8, WI-17): a grown-up signed in on a child's phone (the server says so). "Done",
   * or 30 idle minutes (PARENT_MODE_MINUTES), signs the grown-up out and the child back in.
   */
  returnTo: string | null
  backToChild(): Promise<void>
}

/** Pushes someone's queued events while their session still exists (best effort, online only). */
const flush = (personId: string) => syncNow({ renew: () => Promise.resolve(false), personId: () => personId }).catch(() => undefined)

export const useSession = create<SessionState>((set, get) => ({
  person: null,
  ready: false,
  people: null,
  linkState: 'unknown',
  resident: null,
  pendingCode: joinCode,
  returnTo: null,

  async restore() {
    const person = (await kvGet<Person>('person')) ?? null
    const people = (await kvGet<Person[]>('people')) ?? null
    const returnTo = (await kvGet<string>('returnTo')) ?? null
    const resident = (await kvGet<string>('resident')) ?? null
    if (person) await stampUnownedOutbox(person.id)
    set({ person: person && normalisePerson(person), people: people && people.map(normalisePerson), returnTo, resident, ready: true })
  },

  async loadPeople() {
    try {
      const people = await api<Person[]>('/api/people')
      await kvSet('people', people)
      set({ people, linkState: 'linked' })
    } catch (e) {
      // Offline with a family cached: it was linked, and nothing says otherwise yet.
      if (e instanceof Offline && get().people?.length) set({ linkState: 'linked' })
      const why = authError(e)
      if (why?.error !== 'not linked') throw e
      // The family's first phone (or a dev server) links itself; any other phone needs a code.
      if (why.setup) {
        await get().link()
        return get().loadPeople()
      }
      set({ linkState: 'needs-link' })
      if (get().pendingCode) await get().link(get().pendingCode ?? undefined).catch(() => undefined)
    }
  },

  async link(code) {
    const label = await deviceLabel()
    const body = (id: string) => JSON.stringify({ deviceId: id, code, label })
    try {
      await api('/api/devices/link', { method: 'POST', body: body(await deviceId()) })
    } catch (e) {
      // The parent unlinked this phone before: join again under a new id.
      if (!(e instanceof HttpError && e.status === 409)) throw e
      await api('/api/devices/link', { method: 'POST', body: body(await newDeviceId()) })
    }
    joinCode = null
    set({ linkState: 'linked', pendingCode: null })
    if (code) await get().loadPeople()
  },

  async startFamily(req) {
    const res = await asThisPhone<{ person: Person; recoveryCode: string }>('/api/families', req)
    // The link stays 'needs-link' until `enter`, so the Welcome flow can still show the recovery code.
    joinCode = null
    set({ pendingCode: null })
    return res
  },

  async recover(code) {
    const person = await asThisPhone<Person>('/api/families/recover', { code })
    joinCode = null
    set({ linkState: 'linked', pendingCode: null })
    await get().enter(person)
  },

  async enter(person) {
    await kvSet('person', person)
    await kvSet('returnTo', null)
    // Setting up (or getting back in) on a phone makes it the parent's.
    await kvSet('resident', person.id)
    parentTouch.clear()
    // Started or recovered with a PIN or code just now: the grown-up screens are open (B3).
    organiserTouch.write(Date.now())
    set({ person, returnTo: null, resident: person.id, linkState: 'linked' })
    await get()
      .loadPeople()
      .catch(() => undefined)
  },

  async choose(personId, pin) {
    const res = await api<SessionResponse>('/api/session', {
      method: 'POST',
      body: JSON.stringify({ personId, deviceId: await deviceId(), pin }),
    })
    const person = personOf(res)
    // The server decides parent mode (WI-17): a grown-up on a phone whose resident is a child.
    const resident = res.resident ?? null
    const returnTo = res.parentMode ? resident : null
    await kvSet('person', person)
    await kvSet('returnTo', returnTo)
    await kvSet('resident', resident)
    // Parent mode's countdown starts now (renewing the same grown-up's session keeps it going).
    if (!returnTo) parentTouch.clear()
    else if (get().person?.id !== person.id || !parentTouch.read()) parentTouch.write(Date.now())
    // A PIN just typed opens the grown-up screens without asking again (B3, domain/organiserLock).
    if (pin && person.kind !== 'child') organiserTouch.write(Date.now())
    set({ person, returnTo, resident })
  },

  async switchTo(personId, pin) {
    const me = get().person
    // Re-picking yourself changes nothing: no sign-out, no new session.
    if (me?.id === personId) return
    // Someone else: the current person's queued events go up while their session still exists,
    // then the new pick replaces that session on the server (worker/session startSession). A
    // refused PIN leaves the current person signed in, so ✕ still goes back to them.
    if (me) await flush(me.id)
    await get().choose(personId, pin)
  },

  async makeResident(personId) {
    const res = await api<SessionResponse>('/api/devices/this/resident', { method: 'PUT', body: JSON.stringify({ personId }) })
    const returnTo = res.parentMode ? res.resident : null
    await kvSet('resident', res.resident)
    await kvSet('returnTo', returnTo)
    if (!returnTo) parentTouch.clear()
    else if (!parentTouch.read()) parentTouch.write(Date.now())
    set({ resident: res.resident, returnTo })
  },

  async backToChild() {
    const child = get().returnTo
    await get().forget()
    await kvSet('returnTo', null)
    parentTouch.clear()
    set({ returnTo: null })
    // Offline the child can't get a fresh session: the Who screen shows and they tap themselves.
    if (child) await get().choose(child).catch((e: unknown) => console.warn('parent mode: the child could not sign back in', e))
  },

  async forget() {
    const me = get().person?.id
    if (me) await flush(me)
    // A refusal is worth knowing about (the session lives on until it expires), so it's logged.
    await api('/api/session', { method: 'DELETE' }).catch((e: unknown) => console.warn('sign-out: the server did not end the session', e))
    await kvSet('person', null)
    set({ person: null })
  },

  async renew() {
    const p = get().person
    if (!p) return false
    // Parent mode's session ran out on the server: the phone goes back to the child. Not awaited:
    // renew runs inside a sync pass, and signing out waits for that pass to finish.
    if (get().returnTo) {
      void get().backToChild()
      return false
    }
    try {
      await get().choose(p.id)
      return true
    } catch (e) {
      // The parent's PIN (or a new link) is needed: back to the Who screen. Queued events wait.
      const why = authError(e)?.error
      if (why === 'pin required' || why === 'not linked' || why === 'set a PIN first') {
        await kvSet('person', null)
        set({ person: null })
      }
      return false
    }
  },

  async refreshMe() {
    const me = await api<Person>('/api/me')
    await kvSet('person', me)
    const people = get().people?.map((p) => (p.id === me.id ? me : p)) ?? null
    if (people) await kvSet('people', people)
    set({ person: me, people })
  },
}))
